import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Tag,
  User,
  Calendar
} from 'lucide-react';
import { TeamDecisionService } from '@/services/team-decision.service';
import { Decision, DecisionStatus, DecisionImpact, DecisionStatusLabels, DecisionImpactLabels } from '@/models/team-decision.model';

export const DecisionsBoard: React.FC = () => {
  const navigate = useNavigate();
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<DecisionStatus | 'all'>('all');
  const [filterImpact, setFilterImpact] = useState<DecisionImpact | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedDecisions, setExpandedDecisions] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadDecisions();
  }, []);

  const loadDecisions = async () => {
    try {
      setLoading(true);
      const allDecisions = await TeamDecisionService.getAllDecisions();
      setDecisions(allDecisions);
    } catch (error) {
      console.error('Error loading decisions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredDecisions = () => {
    return decisions.filter(decision => {
      const matchesSearch = searchTerm === '' ||
        decision.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        decision.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || decision.status === filterStatus;
      const matchesImpact = filterImpact === 'all' || decision.impact === filterImpact;

      return matchesSearch && matchesStatus && matchesImpact;
    });
  };

  const getStatusBadge = (status: DecisionStatus) => {
    const badges = {
      [DecisionStatus.draft]: { color: 'bg-gray-100 text-gray-800', icon: Clock },
      [DecisionStatus.pendingApproval]: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      [DecisionStatus.approved]: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      [DecisionStatus.rejected]: { color: 'bg-red-100 text-red-800', icon: XCircle },
      [DecisionStatus.implemented]: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      [DecisionStatus.reversed]: { color: 'bg-purple-100 text-purple-800', icon: AlertTriangle },
    };
    return badges[status];
  };

  const getImpactBadge = (impact: DecisionImpact) => {
    const badges = {
      [DecisionImpact.low]: 'bg-gray-100 text-gray-800',
      [DecisionImpact.medium]: 'bg-blue-100 text-blue-800',
      [DecisionImpact.high]: 'bg-orange-100 text-orange-800',
      [DecisionImpact.critical]: 'bg-red-100 text-red-800',
    };
    return badges[impact];
  };

  const toggleExpanded = (decisionId: string) => {
    const newExpanded = new Set(expandedDecisions);
    if (newExpanded.has(decisionId)) {
      newExpanded.delete(decisionId);
    } else {
      newExpanded.add(decisionId);
    }
    setExpandedDecisions(newExpanded);
  };

  const handleApprove = async (decisionId: string) => {
    try {
      await TeamDecisionService.approveDecision(decisionId, 'current-user-id', 'Current User');
      await loadDecisions();
    } catch (error) {
      console.error('Error approving decision:', error);
    }
  };

  const handleReject = async (decisionId: string) => {
    try {
      const reason = prompt('Please provide a reason for rejection:');
      if (reason) {
        await TeamDecisionService.rejectDecision(decisionId, 'current-user-id', 'Current User', reason);
        await loadDecisions();
      }
    } catch (error) {
      console.error('Error rejecting decision:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading decisions...</p>
        </div>
      </div>
    );
  }

  const filteredDecisions = getFilteredDecisions();

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/team-workspace')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Decisions</h1>
            <p className="text-gray-600 mt-1">Track and approve team decisions</p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search decisions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Status:</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as DecisionStatus | 'all')}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Statuses</option>
                  {Object.values(DecisionStatus).map((status) => (
                    <option key={status} value={status}>
                      {DecisionStatusLabels[status]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Impact:</label>
                <select
                  value={filterImpact}
                  onChange={(e) => setFilterImpact(e.target.value as DecisionImpact | 'all')}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Impacts</option>
                  {Object.values(DecisionImpact).map((impact) => (
                    <option key={impact} value={impact}>
                      {DecisionImpactLabels[impact]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Decisions List */}
      <div className="space-y-4">
        {filteredDecisions.length === 0 ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
            <CheckCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No decisions found</p>
            <p className="text-gray-500 text-sm mt-2">Upload a meeting to extract decisions</p>
          </div>
        ) : (
          filteredDecisions.map((decision) => {
            const statusBadge = getStatusBadge(decision.status);
            const StatusIcon = statusBadge.icon;
            const isExpanded = expandedDecisions.has(decision.id);

            return (
              <div
                key={decision.id}
                className="bg-white rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow"
              >
                {/* Decision Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.color} flex items-center`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {DecisionStatusLabels[decision.status]}
                        </span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getImpactBadge(decision.impact)}`}>
                          {DecisionImpactLabels[decision.impact]} Impact
                        </span>
                        {decision.containsPHI && (
                          <span className="px-2.5 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium flex items-center">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            PHI
                          </span>
                        )}
                      </div>

                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {decision.title}
                      </h3>

                      <p className="text-gray-600 mb-4">
                        {decision.description}
                      </p>

                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <div className="flex items-center">
                          <User className="w-4 h-4 mr-1" />
                          {decision.decisionMaker}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(decision.createdAt).toLocaleDateString()}
                        </div>
                        <div className="flex items-center">
                          <Tag className="w-4 h-4 mr-1" />
                          {decision.category}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => toggleExpanded(decision.id)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-600" />
                      )}
                    </button>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
                      {decision.rationale && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Rationale</h4>
                          <p className="text-gray-600">{decision.rationale}</p>
                        </div>
                      )}

                      {decision.alternatives && decision.alternatives.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Alternatives Considered</h4>
                          <ul className="list-disc list-inside space-y-1">
                            {decision.alternatives.map((alt, idx) => (
                              <li key={idx} className="text-gray-600">{alt}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {decision.affectedParties && decision.affectedParties.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Affected Parties</h4>
                          <div className="flex flex-wrap gap-2">
                            {decision.affectedParties.map((party, idx) => (
                              <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                                {party}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {decision.sourceTextSnippet && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Source Context</h4>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 italic">"{decision.sourceTextSnippet}"</p>
                            <p className="text-xs text-gray-500 mt-2">From: {decision.meetingTitle}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  {decision.status === DecisionStatus.pendingApproval && (
                    <div className="mt-6 pt-6 border-t border-gray-200 flex items-center space-x-3">
                      <button
                        onClick={() => handleApprove(decision.id)}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(decision.id)}
                        className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Total Decisions</div>
          <div className="text-2xl font-bold text-gray-900">{decisions.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Pending Approval</div>
          <div className="text-2xl font-bold text-yellow-600">
            {decisions.filter(d => d.status === DecisionStatus.pendingApproval).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Approved</div>
          <div className="text-2xl font-bold text-green-600">
            {decisions.filter(d => d.status === DecisionStatus.approved).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Implemented</div>
          <div className="text-2xl font-bold text-blue-600">
            {decisions.filter(d => d.status === DecisionStatus.implemented).length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionsBoard;
